import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import "./Timeline.css";

// 2026–27 season, summarized from the team's build-and-buy Gantt sheet. Keep it
// high-level (no technical approach details) and use TBC's published wording for
// deliverable deadlines that aren't public yet (dateLabel overrides the dates shown).
const timeline2026 = [
  {
    category: "TBC Deliverable",
    taskName: "Submitted our Preliminary Design Briefing",
    startDate: "9/6/2026",
    endDate: "9/13/2026",
  },
  {
    category: "TBC Deliverable",
    taskName: "Final Design Package",
    startDate: "11/1/2026",
    endDate: "12/31/2026",
    dateLabel: "Due winter 2026",
  },
  {
    category: "TBC Deliverable",
    taskName: "Mining Readiness Review",
    startDate: "3/1/2027",
    endDate: "3/31/2027",
    dateLabel: "Spring 2027",
  },
  {
    category: "Mechanical Design",
    taskName:
      "Cutterhead design • Main bearing housing and structures • Slurry conditioning, lubrication, and muck handling • Pipe-jacking equipment",
    startDate: "9/13/2026",
    endDate: "11/15/2026",
  },
  {
    category: "Electrical",
    taskName:
      "Power and controls schematics • Electrical bill of materials • Purchase electrical components",
    startDate: "9/13/2026",
    endDate: "11/8/2026",
  },
  {
    category: "Hydraulics",
    taskName:
      "Hydraulic schematic • Select and spec hydraulic components • Hydraulics build plan",
    startDate: "9/27/2026",
    endDate: "11/15/2026",
  },
  {
    category: "Procurement",
    taskName:
      "Sponsorship outreach • Purchase components and materials • Long-lead parts and steel",
    startDate: "9/20/2026",
    endDate: "1/3/2027",
  },
  {
    category: "Build Planning",
    taskName: "Build plans for every subsystem, ready for a winter-break build",
    startDate: "10/18/2026",
    endDate: "11/15/2026",
  },
];

const timeline2025 = [
  {
    category: "Funding",
    taskName: "Raise $10k • Software, CAD, and simulation tools necessary for designs",
    owner: "",
    startDate: "6/1/2025",
    endDate: "7/31/2025",
  },

  {
    category: "Funding",
    taskName:
      "Raise $10k-$30k (Including massive support from University of Austin) • Conditioners and soil types, secure storage and testing space • All mucking + power costs • All remaining conditioning costs • All cooling costs • All cutterhead + shop use costs • All motor assembly costs • All telemetry costs • All thrust costs • All safety costs • All transportation costs",
    owner: "",
    startDate: "8/1/2025",
    endDate: "9/30/2025",
  },
  {
    category: "Safety + Full Tests",
    taskName:
      "Fulfill shop safety requirements, schedule necessary shop classes • Arrive at storage and testing space in Austin • Pick up and store all funded parts/equipment",
    owner: "",
    startDate: "6/28/2025",
    endDate: "9/8/2025",
  },

  {
    category: "TBC Deliverable",
    taskName: "Submit Mid-Summer Check In",
    owner: "",
    startDate: "7/10/2025",
    endDate: "7/14/2025",
  },
  {
    category: "Iterative Design",
    taskName:
      "CAD Motor Assembly • CAD Bottom Disc + Cutterhead • Simulate Spin w/ FEA (various soil conditions) • CAD Top Disc + Linear Actuator Mounts • Simulate Thrust + Spin w/ FEA • CAD Steel Anchor Cage + Thrust System • Simulate Full Load w/ FEA • CAD Spray Nozzle (integration w/ load bearing) • Simulate Spray Nozzle w/ CFD • CAD Conditioning + Mucking Systems • Simulate Conditioning + Mucking Systems w/ CFD • CAD Recycling System • Simulate Recycling System w/ CFD • Simulate Complete System w/ CFD and FEA • CAD Field Site (outline operator tent, power/fluid systems) • Complete I/O list and state diagram • Design operator GUI mock-up • Continue CAD/Simulation Iterations • Take necessary shop classes",
    owner: "",
    startDate: "6/28/2025",
    endDate: "11/21/2025",
  },

  {
    category: "TBC Deliverable",
    taskName: "Submit Preliminary Design Briefing",
    owner: "",
    startDate: "8/30/2025",
    endDate: "9/8/2025",
  },
  {
    category: "Prototype",
    taskName:
      "$1k for 9500W generator • $1k in parts for the rest of the prototype, most of which can be used for the final machine • Goal: dig a hole 9 inches wide and at least a foot deep",
    owner: "",
    startDate: "9/22/2025",
    endDate: "11/9/2025",
  },
  {
    category: "TBC Deliverable",
    taskName: "Submit Final Design Package (120 pages)",
    owner: "",
    startDate: "10/20/2025",
    endDate: "11/3/2025",
  },
  {
    category: "Final Machine Build",
    taskName: "Final machine build",
    owner: "",
    startDate: "11/16/2025",
    endDate: "1/15/2026",
  },
  {
    category: "Safety + Full Tests",
    taskName:
      "Wrap in the chassis and do full system tests with safety procedures",
    owner: "",
    startDate: "12/6/2025",
    endDate: "3/1/2026",
  },
];

const parseDate = (dateStr) => new Date(dateStr);

const formatDate = (dateStr) =>
  parseDate(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

const taskDates = (task) =>
  task.dateLabel ||
  `${formatDate(task.startDate)} – ${formatDate(task.endDate)}`;

// Overall date range of a season and the months it spans
const buildSeason = (season) => {
  const allDates = season.tasks.flatMap((item) => [
    parseDate(item.startDate),
    parseDate(item.endDate),
  ]);
  const minDate = new Date(Math.min(...allDates));
  const maxDate = new Date(Math.max(...allDates));

  const timePeriods = [];
  for (
    let month = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
    month <= maxDate;
    month.setMonth(month.getMonth() + 1)
  ) {
    timePeriods.push(new Date(month));
  }

  const getTaskPosition = (task) => {
    const totalDuration = maxDate - minDate;
    const taskStart = parseDate(task.startDate) - minDate;
    const taskDuration = parseDate(task.endDate) - parseDate(task.startDate);

    return {
      left: (taskStart / totalDuration) * 100,
      width: Math.max((taskDuration / totalDuration) * 100, 0.5), // Minimum 0.5% width
    };
  };

  return {
    ...season,
    categories: Object.keys(season.categoryColors),
    timePeriods,
    getTaskPosition,
  };
};

const seasons = [
  {
    label: "2026–27",
    note: "Build, test, and competition dates will be added as the schedule firms up.",
    categoryColors: {
      "TBC Deliverable": "#FF9800",
      "Mechanical Design": "#E91E63",
      Electrical: "#03A9F4",
      Hydraulics: "#9C27B0",
      Procurement: "#4CAF50",
      "Build Planning": "#8BC34A",
    },
    tasks: timeline2026,
  },
  {
    label: "2025–26",
    categoryColors: {
      Funding: "#4CAF50",
      "TBC Deliverable": "#FF9800",
      "Iterative Design": "#9C27B0",
      Prototype: "#E91E63",
      "Final Machine Build": "#3F51B5",
      "Safety + Full Tests": "#8BC34A",
    },
    tasks: timeline2025,
  },
].map(buildSeason);

const TaskPopup = ({ task, onClose }) => {
  // Close on Escape and keep the page from scrolling behind the popup
  useEffect(() => {
    const handleKey = (e) => e.key === "Escape" && onClose();
    const previousOverflow = document.body.style.overflow;
    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  // Rendered into <body> so no transformed ancestor can trap the fixed overlay
  return createPortal(
    <div className="task-details" onClick={onClose}>
      <div
        className="task-popup"
        role="dialog"
        aria-modal="true"
        aria-labelledby="task-popup-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="close-btn"
          onClick={onClose}
          aria-label="Close"
          autoFocus
        >
          ×
        </button>
        <h4 id="task-popup-title">{task.category}</h4>
        <p className="task-dates">{taskDates(task)}</p>
        {task.owner && <p className="task-owner">Owner: {task.owner}</p>}
        <ul className="task-description">
          {task.taskName.split("•").map((item, index) => (
            <li key={index} className="task-item">
              {item.trim()}
            </li>
          ))}
        </ul>
      </div>
    </div>,
    document.body
  );
};

const Timeline = () => {
  const [season, setSeason] = useState(seasons[0]);
  const [selectedTask, setSelectedTask] = useState(null);
  const closePopup = React.useCallback(() => setSelectedTask(null), []);
  const { categories, categoryColors, timePeriods, getTaskPosition } = season;

  return (
    <section className="timeline-section">
      <h2 className="timeline-title">Project Timeline</h2>
      <div className="season-toggle" role="group" aria-label="Season">
        {seasons.map((s) => (
          <button
            key={s.label}
            type="button"
            className="season-button"
            aria-pressed={s === season}
            onClick={() => setSeason(s)}
          >
            {s.label}
          </button>
        ))}
      </div>
      {season.note && <p className="timeline-season">{season.note}</p>}
      <p className="timeline-hint">
        <span className="hint-touch">Swipe to scroll · tap</span>
        <span className="hint-pointer">Click</span> a bar for details
      </p>

      <div className="gantt-container">
        <div className="gantt-inner">
          {/* Time axis - horizontal across the top */}
          <div className="time-axis">
            <div className="time-axis-corner" />
            {timePeriods.map((period) => (
              <div key={period.toISOString()} className="time-period">
                <span className="month-long">
                  {period.toLocaleDateString("en-US", { month: "long" })}
                </span>
                <span className="month-short">
                  {period.toLocaleDateString("en-US", { month: "short" })}
                </span>
              </div>
            ))}
          </div>

          {/* One row per category: label on the left, task bars on the right */}
          <div className="gantt-chart">
            {categories.map((category) => (
              <div key={category} className="chart-row">
                <div className="chart-header">
                  <span
                    className="header-swatch"
                    style={{ backgroundColor: categoryColors[category] }}
                  />
                  <span className="header-text">{category}</span>
                </div>
                <div className="chart-track">
                  {season.tasks
                    .filter((task) => task.category === category)
                    .map((task) => {
                      const position = getTaskPosition(task);
                      return (
                        <button
                          key={`${task.startDate}-${task.endDate}`}
                          type="button"
                          className={`task-bar ${
                            selectedTask === task ? "selected" : ""
                          }`}
                          style={{
                            left: `${position.left}%`,
                            width: `${position.width}%`,
                            backgroundColor: categoryColors[category],
                          }}
                          onClick={() => setSelectedTask(task)}
                          aria-label={`${category}: ${task.taskName
                            .split("•")[0]
                            .trim()} (${taskDates(task)})`}
                        />
                      );
                    })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {selectedTask && <TaskPopup task={selectedTask} onClose={closePopup} />}
    </section>
  );
};

export default Timeline;
